import {
  CalendarDays,
  Check,
  Clock,
  Edit3,
  FileText,
  Filter,
  GraduationCap,
  Layers3,
  Moon,
  Palette,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings2,
  Sun,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCurrentUser,
  loadRemoteCalendar,
  loadRemoteState,
  login,
  logout,
  register,
  saveRemoteCalendar,
  saveRemoteState,
  type LessonThemeGroup,
  type UserAccount
} from "./api";
import {
  colorOptions,
  defaultDepartments,
  loadDepartments,
  persistDepartments,
  slugify,
  splitCodes,
  tintColor
} from "./departments";
import { parseIcalFile, rematchDepartments } from "./ical";
import type { AnalyticsMetric, AnalyticsRange, AnalyticsScope, Department, DepartmentDraft, LessonEvent, SortMode, UploadSummary } from "./types";

type FilterId = "all" | "unknown" | string;
type ViewMode = "list" | "compact";
type ThemeMode = "light" | "dark";

type RawLessonTheme = {
  label: string;
  lessons: number;
  hours: number;
};

const LESSON_THEME_STORAGE_KEY = "lessonIcalThemeAliases:v1";

const emptyDraft: DepartmentDraft = {
  name: "",
  codes: "",
  color: "#64748b",
  parentId: "",
  isShortCourse: false
};

export function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => loadThemeMode());
  const [departments, setDepartments] = useState<Department[]>(loadDepartments);
  const [events, setEvents] = useState<LessonEvent[]>([]);
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<FilterId>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortMode>("date");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [groupSubDepartments, setGroupSubDepartments] = useState(true);
  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>("12m");
  const [analyticsScope, setAnalyticsScope] = useState<AnalyticsScope>("past");
  const [analyticsMetric, setAnalyticsMetric] = useState<AnalyticsMetric>("lessons");
  const [lessonThemeGroups, setLessonThemeGroups] = useState<LessonThemeGroup[]>(loadLessonThemeGroups);
  const [hourlyWage, setHourlyWage] = useState(() => localStorage.getItem("lesson-hourly-wage") || "");
  const [currentUser, setCurrentUser] = useState<UserAccount | null>(null);
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [syncStatus, setSyncStatus] = useState("Local only");
  const [remoteReady, setRemoteReady] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [dragging, setDragging] = useState(false);
  const [departmentDraft, setDepartmentDraft] = useState<DepartmentDraft | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const latestCalendarRef = useRef<{ fileName: string; content: string } | null>(null);
  const today = useMemo(() => startOfToday(), []);
  const visibleDepartments = useMemo(() => (groupSubDepartments ? departments.filter((department) => !department.parentId) : departments), [departments, groupSubDepartments]);
  const upcomingEvents = useMemo(() => events.filter((event) => event.start >= today), [events, today]);
  const pastEvents = useMemo(() => events.filter((event) => event.start < today), [events, today]);
  const analyticsWindow = useMemo(() => getAnalyticsWindow(events, today, analyticsRange, analyticsScope, customStart, customEnd), [events, today, analyticsRange, analyticsScope, customStart, customEnd]);
  const analyticsEvents = useMemo(() => events.filter((event) => event.start >= analyticsWindow.start && event.start <= analyticsWindow.end), [events, analyticsWindow]);

  const unknownCodes = useMemo(() => {
    const counts = new Map<string, number>();
    upcomingEvents.forEach((event) => {
      const code = event.moduleCode || event.code;
      if (!event.department && code) counts.set(code, (counts.get(code) || 0) + 1);
    });
    return [...counts.entries()].map(([code, count]) => ({ code, count })).sort((a, b) => a.code.localeCompare(b.code));
  }, [upcomingEvents]);

  const visibleEvents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return upcomingEvents
      .filter((event) => {
        const matchesDepartment =
          selectedDepartmentId === "all" ||
          (selectedDepartmentId === "unknown" && !event.department) ||
          getDisplayDepartment(event, departments, groupSubDepartments)?.id === selectedDepartmentId ||
          (!groupSubDepartments && event.department?.parentId === selectedDepartmentId);

        const searchText = [
          event.topic,
          event.moduleCode,
          event.code,
          event.delivery,
          event.department?.name,
          getDisplayDepartment(event, departments, groupSubDepartments)?.name,
          event.summary
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return matchesDepartment && (!normalizedSearch || searchText.includes(normalizedSearch));
      })
      .sort((a, b) => {
        if (sort === "department") {
          return (getDisplayDepartment(a, departments, groupSubDepartments)?.name || "Unrecognized").localeCompare(getDisplayDepartment(b, departments, groupSubDepartments)?.name || "Unrecognized") || a.start.getTime() - b.start.getTime();
        }
        if (sort === "topic") {
          return (a.topic || a.summary).localeCompare(b.topic || b.summary) || a.start.getTime() - b.start.getTime();
        }
        return a.start.getTime() - b.start.getTime();
      });
  }, [upcomingEvents, search, selectedDepartmentId, sort, departments, groupSubDepartments]);

  const eventsByDay = useMemo(() => groupByDay(visibleEvents), [visibleEvents]);
  const activeDepartmentCount = new Set(upcomingEvents.map((event) => getDisplayDepartment(event, departments, groupSubDepartments)?.id).filter(Boolean)).size;
  const nextLesson = upcomingEvents[0];
  const analyticsTotal = useMemo(() => sumMetric(analyticsEvents, analyticsMetric), [analyticsEvents, analyticsMetric]);
  const departmentInsights = useMemo(() => buildDepartmentInsights(analyticsEvents, departments, groupSubDepartments, analyticsMetric), [analyticsEvents, departments, groupSubDepartments, analyticsMetric]);
  const monthlyInsights = useMemo(() => buildMonthlyInsights(analyticsEvents, analyticsWindow.start, analyticsWindow.end, analyticsMetric), [analyticsEvents, analyticsWindow, analyticsMetric]);
  const rawLessonThemes = useMemo(() => buildRawLessonThemes(analyticsEvents), [analyticsEvents]);
  const topicInsights = useMemo(() => buildTopicInsights(analyticsEvents, analyticsMetric, lessonThemeGroups), [analyticsEvents, analyticsMetric, lessonThemeGroups]);
  const weekdayInsights = useMemo(() => buildWeekdayInsights(analyticsEvents, analyticsMetric), [analyticsEvents, analyticsMetric]);
  const deliveryInsights = useMemo(() => buildDeliveryInsights(analyticsEvents, analyticsMetric), [analyticsEvents, analyticsMetric]);
  const earnings = useMemo(() => buildEarningsSummary(analyticsEvents, today, hourlyWage), [analyticsEvents, today, hourlyWage]);

  useEffect(() => {
    document.documentElement.dataset.theme = themeMode;
    localStorage.setItem("lesson-theme-mode", themeMode);
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem("lesson-hourly-wage", hourlyWage);
  }, [hourlyWage]);

  useEffect(() => {
    void bootRemoteSession();
  }, []);

  useEffect(() => {
    if (!currentUser || !remoteReady) return;
    setSyncStatus("Saving...");
    const handle = window.setTimeout(() => {
      void saveRemoteState({
        departments,
        themeGroups: lessonThemeGroups,
        settings: {
          analyticsMetric,
          analyticsRange,
          analyticsScope,
          groupSubDepartments,
          hourlyWage,
          themeMode
        }
      })
        .then(() => setSyncStatus("Saved"))
        .catch((saveError) => setSyncStatus(saveError instanceof Error ? saveError.message : "Save failed"));
    }, 700);
    return () => window.clearTimeout(handle);
  }, [analyticsMetric, analyticsRange, analyticsScope, currentUser, departments, groupSubDepartments, hourlyWage, lessonThemeGroups, remoteReady, themeMode]);

  async function bootRemoteSession() {
    try {
      const result = await getCurrentUser();
      if (!result.user) {
        setSyncStatus("Local only");
        return;
      }
      setCurrentUser(result.user);
      await hydrateRemoteData();
    } catch {
      setSyncStatus("Local only");
    }
  }

  async function hydrateRemoteData() {
    setSyncStatus("Loading saved data...");
    const [stateResult, calendarResult] = await Promise.all([loadRemoteState(), loadRemoteCalendar()]);
    const savedState = stateResult.state;
    const savedDepartments = savedState?.departments?.length ? savedState.departments : departments;

    if (savedState?.departments?.length) {
      setDepartments(savedDepartments);
      persistDepartments(savedDepartments);
    }
    if (savedState?.themeGroups) {
      setLessonThemeGroups(savedState.themeGroups);
      persistLessonThemeGroups(savedState.themeGroups);
    }
    if (savedState?.settings) {
      if (savedState.settings.themeMode) setThemeMode(savedState.settings.themeMode);
      if (savedState.settings.hourlyWage != null) setHourlyWage(String(savedState.settings.hourlyWage));
      if (savedState.settings.groupSubDepartments != null) setGroupSubDepartments(Boolean(savedState.settings.groupSubDepartments));
      if (savedState.settings.analyticsRange) setAnalyticsRange(savedState.settings.analyticsRange);
      if (savedState.settings.analyticsScope) setAnalyticsScope(savedState.settings.analyticsScope);
      if (savedState.settings.analyticsMetric) setAnalyticsMetric(savedState.settings.analyticsMetric);
    }
    if (calendarResult.calendar) {
      latestCalendarRef.current = { fileName: calendarResult.calendar.fileName, content: calendarResult.calendar.content };
      loadIcalContent(calendarResult.calendar.content, calendarResult.calendar.fileName, savedDepartments);
    }
    setRemoteReady(true);
    setSyncStatus("Saved");
  }

  async function importFile(file: File) {
    setError("");
    try {
      const text = await file.text();
      latestCalendarRef.current = { fileName: file.name, content: text };
      loadIcalContent(text, file.name, departments);
      if (currentUser) {
        setSyncStatus("Saving calendar...");
        await saveRemoteCalendar({ fileName: file.name, content: text });
        setSyncStatus("Saved");
      }
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : "The iCal file could not be read.");
    }
  }

  function loadIcalContent(content: string, fileName: string, activeDepartments: Department[]) {
    const result = parseIcalFile(content, activeDepartments, fileName);
    setEvents(result.events);
    setUploadSummary(result.summary);
    setSelectedDepartmentId("all");
    setExpandedEventId(null);
  }

  async function submitAuth() {
    setAuthError("");
    try {
      const result = authMode === "login" ? await login(authEmail, authPassword) : await register(authEmail, authPassword);
      setCurrentUser(result.user);
      setAuthPassword("");
      setRemoteReady(false);
      await hydrateRemoteData();

      if (latestCalendarRef.current) {
        await saveRemoteCalendar(latestCalendarRef.current);
      }
    } catch (submitError) {
      setAuthError(submitError instanceof Error ? submitError.message : "Authentication failed.");
    }
  }

  async function signOut() {
    await logout();
    setCurrentUser(null);
    setRemoteReady(false);
    setSyncStatus("Local only");
  }

  function updateDepartments(nextDepartments: Department[]) {
    setDepartments(nextDepartments);
    persistDepartments(nextDepartments);
    setEvents((currentEvents) => rematchDepartments(currentEvents, nextDepartments));
  }

  function updateLessonThemeGroups(nextGroups: LessonThemeGroup[]) {
    setLessonThemeGroups(nextGroups);
    persistLessonThemeGroups(nextGroups);
  }

  function saveDepartment() {
    if (!departmentDraft) return;
    const name = departmentDraft.name.trim();
    const codes = splitCodes(departmentDraft.codes);
    if (!name || !codes.length) return;

    const id = departmentDraft.id || slugify(`${name}-${codes.join("-")}`);
    const nextDepartment: Department = {
      id,
      name,
      codes,
      color: departmentDraft.color || "#64748b",
      parentId: departmentDraft.parentId || undefined,
      isShortCourse: Boolean(departmentDraft.isShortCourse)
    };

    const exists = departments.some((department) => department.id === id);
    const nextDepartments = exists
      ? departments.map((department) => (department.id === id ? { ...department, ...nextDepartment, builtin: department.builtin } : department))
      : [...departments, nextDepartment];

    updateDepartments(nextDepartments);
    setDepartmentDraft(null);
  }

  function deleteDepartment(id: string) {
    const nextDepartments = departments.filter((department) => department.id !== id);
    updateDepartments(nextDepartments);
    if (selectedDepartmentId === id) setSelectedDepartmentId("all");
    setDepartmentDraft(null);
  }

  function resetDepartments() {
    updateDepartments(defaultDepartments);
    setSelectedDepartmentId("all");
  }

  return (
    <main className="app-shell">
      <header className="app-header">
        <div>
          <p className="eyebrow">iCal lesson planner</p>
          <h1>Upcoming lessons</h1>
        </div>
        <div className="header-actions">
          <button className="secondary-button" type="button" onClick={() => setThemeMode(themeMode === "dark" ? "light" : "dark")}>
            {themeMode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            {themeMode === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <button className="secondary-button" type="button" onClick={() => setViewMode(viewMode === "list" ? "compact" : "list")}>
            <Layers3 size={18} />
            {viewMode === "list" ? "Compact" : "Detailed"}
          </button>
          <button className="primary-button" type="button" onClick={() => fileInputRef.current?.click()}>
            <Upload size={18} />
            Upload iCal
          </button>
          <input ref={fileInputRef} className="sr-only" type="file" accept=".ics,text/calendar" onChange={(event) => event.target.files?.[0] && importFile(event.target.files[0])} />
        </div>
      </header>

      <section className="layout">
        <aside className="sidebar">
          <section
            className={`drop-panel ${dragging ? "dragging" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              const file = event.dataTransfer.files[0];
              if (file) void importFile(file);
            }}
          >
            <div className="drop-icon">
              <FileText size={26} />
            </div>
            <h2>Upload a lesson schedule</h2>
            <p>Drop an `.ics` file here to import upcoming lessons and detect department codes.</p>
            <button className="link-button" type="button" onClick={() => fileInputRef.current?.click()}>
              Choose file
            </button>
            {uploadSummary && <p className="file-note">{uploadSummary.fileName}</p>}
            {error && <p className="error-note">{error}</p>}
          </section>

          <AuthPanel
            email={authEmail}
            error={authError}
            mode={authMode}
            password={authPassword}
            status={syncStatus}
            user={currentUser}
            onEmailChange={setAuthEmail}
            onModeChange={setAuthMode}
            onPasswordChange={setAuthPassword}
            onSubmit={submitAuth}
            onSignOut={signOut}
          />

          <section className="panel">
            <div className="panel-head">
              <h2>Filters</h2>
              <Filter size={18} />
            </div>
            <div className="filter-list">
              <DepartmentFilter
                active={selectedDepartmentId === "all"}
                color="#202124"
                count={upcomingEvents.length}
                label="All departments"
                onClick={() => setSelectedDepartmentId("all")}
              />
              <DepartmentFilter
                active={selectedDepartmentId === "unknown"}
                color="#8b8d94"
                count={upcomingEvents.filter((event) => !event.department).length}
                label="Unrecognized"
                onClick={() => setSelectedDepartmentId("unknown")}
              />
              {departments.map((department) => (
                visibleDepartments.includes(department) && (
                <DepartmentFilter
                  key={department.id}
                  active={selectedDepartmentId === department.id}
                  color={department.color}
                  count={upcomingEvents.filter((event) => getDisplayDepartment(event, departments, groupSubDepartments)?.id === department.id || (!groupSubDepartments && event.department?.id === department.id)).length}
                  label={department.name}
                  detail={formatDepartmentDetail(department, departments)}
                  onClick={() => setSelectedDepartmentId(department.id)}
                  onEdit={() => setDepartmentDraft({ id: department.id, name: department.name, codes: department.codes.join(", "), color: department.color, parentId: department.parentId || "", isShortCourse: Boolean(department.isShortCourse) })}
                />
                )
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Unrecognized codes</h2>
              <span className="count-pill">{unknownCodes.length}</span>
            </div>
            {unknownCodes.length ? (
              <div className="unknown-list">
                {unknownCodes.map((item) => (
                  <button
                    key={item.code}
                    className="unknown-row"
                    type="button"
                    onClick={() => setDepartmentDraft({ ...emptyDraft, codes: item.code })}
                  >
                    <span className="swatch neutral" />
                    <span>
                      <strong>{item.code}</strong>
                      <small>
                        {item.count} upcoming lesson{item.count === 1 ? "" : "s"}
                      </small>
                    </span>
                    <Plus size={18} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="muted">Every detected code has a department.</p>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>Departments</h2>
              <Settings2 size={18} />
            </div>
            <div className="department-actions">
              <button className="secondary-button full" type="button" onClick={() => setDepartmentDraft(emptyDraft)}>
                <Plus size={18} />
                Add department
              </button>
              <button className="ghost-button full" type="button" onClick={resetDepartments}>
                <RotateCcw size={18} />
                Reset defaults
              </button>
              <button className={`ghost-button full ${groupSubDepartments ? "active-mode" : ""}`} type="button" onClick={() => setGroupSubDepartments((value) => !value)}>
                <Layers3 size={18} />
                {groupSubDepartments ? "Grouped view" : "Separate view"}
              </button>
            </div>
          </section>
        </aside>

        <section className="content">
          <section className="stats-grid">
            <StatCard label="Upcoming lessons" value={upcomingEvents.length} icon={<CalendarDays size={22} />} />
            <StatCard label="Active departments" value={activeDepartmentCount} icon={<GraduationCap size={22} />} />
            <StatCard label="Needs mapping" value={unknownCodes.length} icon={<Palette size={22} />} />
            <StatCard label="Past lessons" value={pastEvents.length} icon={<Check size={22} />} />
          </section>

          <section className="control-strip">
            <label className="search-field">
              <Search size={18} />
              <input value={search} type="search" placeholder="Search module, topic, code, department..." onChange={(event) => setSearch(event.target.value)} />
            </label>
            <div className="sort-buttons" aria-label="Sort lessons">
              <button className={sort === "date" ? "active" : ""} type="button" onClick={() => setSort("date")}>Date</button>
              <button className={sort === "department" ? "active" : ""} type="button" onClick={() => setSort("department")}>Dept</button>
              <button className={sort === "topic" ? "active" : ""} type="button" onClick={() => setSort("topic")}>Topic</button>
            </div>
          </section>

          {uploadSummary && (
            <section className="import-summary">
              <strong>{uploadSummary.importedCount} upcoming lessons imported</strong>
              <span>{uploadSummary.ignoredPastCount} past lessons available for insights</span>
              <span>{uploadSummary.totalCount} lessons in file</span>
              {uploadSummary.missingDateCount > 0 && <span>{uploadSummary.missingDateCount} events skipped because they had invalid dates</span>}
            </section>
          )}

          {nextLesson && (
            <section className="next-lesson">
              <div>
                <p className="eyebrow">Next lesson</p>
                <h2>{nextLesson.summary}</h2>
                <p>
                  {formatDay(nextLesson.start)} · {formatTimeRange(nextLesson)} · {nextLesson.moduleCode}
                </p>
              </div>
              <span style={{ background: nextLesson.department?.color || "#8b8d94" }}>{nextLesson.department?.name || "Unrecognized"}</span>
            </section>
          )}

          {events.length > 0 && (
            <section className="insights-board">
              <div className="insight-panel analytics-controls">
                <div className="panel-head">
                  <h2>Insight range</h2>
                  <span className="muted">{formatMetric(analyticsTotal, analyticsMetric)}</span>
                </div>
                <div className="range-controls">
                  <div className="control-group">
                    <span>Show</span>
                    <div className="sort-buttons">
                      <button className={analyticsScope === "past" ? "active" : ""} type="button" onClick={() => setAnalyticsScope("past")}>Completed</button>
                      <button className={analyticsScope === "future" ? "active" : ""} type="button" onClick={() => setAnalyticsScope("future")}>Upcoming</button>
                      <button className={analyticsScope === "both" ? "active" : ""} type="button" onClick={() => setAnalyticsScope("both")}>All</button>
                    </div>
                  </div>
                  <div className="control-group">
                    <span>Range</span>
                    <div className="sort-buttons">
                      <button className={analyticsRange === "3m" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("3m")}>3 months</button>
                      <button className={analyticsRange === "12m" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("12m")}>1 year</button>
                      <button className={analyticsRange === "all" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("all")}>All time</button>
                      <button className={analyticsRange === "custom" ? "active" : ""} type="button" onClick={() => setAnalyticsRange("custom")}>Custom</button>
                    </div>
                  </div>
                  <div className="control-group">
                    <span>Count by</span>
                    <div className="sort-buttons">
                      <button className={analyticsMetric === "lessons" ? "active" : ""} type="button" onClick={() => setAnalyticsMetric("lessons")}>Lessons</button>
                      <button className={analyticsMetric === "hours" ? "active" : ""} type="button" onClick={() => setAnalyticsMetric("hours")}>Hours</button>
                    </div>
                  </div>
                  {analyticsRange === "custom" && (
                    <div className="custom-dates">
                      <input type="date" value={customStart} onChange={(event) => setCustomStart(event.target.value)} />
                      <input type="date" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} />
                    </div>
                  )}
                </div>
              </div>
              <div className="insight-panel earnings-panel">
                <div className="panel-head">
                  <div>
                    <h2>Earnings estimator</h2>
                    <span className="muted">Uses lesson hours in the selected insight range</span>
                  </div>
                </div>
                <div className="earnings-grid">
                  <label className="wage-field">
                    <span>Hourly wage</span>
                    <div>
                      <span>CHF</span>
                      <input
                        min="0"
                        step="1"
                        inputMode="decimal"
                        type="number"
                        value={hourlyWage}
                        placeholder="0"
                        onChange={(event) => setHourlyWage(event.target.value)}
                      />
                    </div>
                  </label>
                  <EarningCard label="Completed" hours={earnings.pastHours} amount={earnings.pastAmount} />
                  <EarningCard label="Upcoming" hours={earnings.futureHours} amount={earnings.futureAmount} />
                  <EarningCard label="Selected range" hours={earnings.totalHours} amount={earnings.totalAmount} strong />
                </div>
              </div>
              <div className="insight-panel radial-panel">
                <div className="panel-head">
                  <h2>{groupSubDepartments ? "Department families" : "Department mix"}</h2>
                  <span className="count-pill">{formatMetric(analyticsTotal, analyticsMetric)}</span>
                </div>
                <RadialDepartments insights={departmentInsights} metric={analyticsMetric} />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Teaching rhythm</h2>
                  <span className="muted">{formatShortDate(analyticsWindow.start)} - {formatShortDate(analyticsWindow.end)}</span>
                </div>
                <MonthBars months={monthlyInsights} metric={analyticsMetric} />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Teaching day pattern</h2>
                  <span className="muted">{analyticsScope === "future" ? "upcoming" : analyticsScope === "both" ? "all selected" : "completed"}</span>
                </div>
                <WeekdayPattern days={weekdayInsights} metric={analyticsMetric} />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Repeated lesson themes</h2>
                  <span className="muted">selected range</span>
                </div>
                <TopicBars topics={topicInsights} metric={analyticsMetric} />
                <ThemeAliasManager
                  groups={lessonThemeGroups}
                  rawThemes={rawLessonThemes}
                  onChange={updateLessonThemeGroups}
                />
              </div>
              <div className="insight-panel">
                <div className="panel-head">
                  <h2>Delivery shape</h2>
                  <span className="muted">all lessons</span>
                </div>
                <DeliveryTiles items={deliveryInsights} metric={analyticsMetric} />
              </div>
            </section>
          )}

          <section className={`timeline ${viewMode}`}>
            {visibleEvents.length ? (
              [...eventsByDay.entries()].map(([day, dayEvents]) => (
                <div className="day-group" key={day}>
                  <div className="day-head">
                    <div>
                      <strong>{formatDay(new Date(day))}</strong>
                      <span>{dayEvents.length} lessons</span>
                    </div>
                  </div>
                  <div className="lesson-list">
                    {dayEvents.map((event) => (
                      <LessonCard
                        key={event.id}
                        event={event}
                        expanded={expandedEventId === event.id}
                        compact={viewMode === "compact"}
                        onToggle={() => setExpandedEventId(expandedEventId === event.id ? null : event.id)}
                        onAssignCode={() => setDepartmentDraft({ ...emptyDraft, codes: event.moduleCode || event.code })}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState hasUpload={Boolean(uploadSummary)} />
            )}
          </section>
        </section>
      </section>

      {departmentDraft && (
        <DepartmentDialog
          draft={departmentDraft}
          departments={departments}
          onChange={setDepartmentDraft}
          onClose={() => setDepartmentDraft(null)}
          onDelete={departmentDraft.id ? () => deleteDepartment(departmentDraft.id!) : undefined}
          onSave={saveDepartment}
        />
      )}
    </main>
  );
}

function EarningCard({ amount, hours, label, strong = false }: { amount: number; hours: number; label: string; strong?: boolean }) {
  return (
    <div className={`earning-card ${strong ? "strong" : ""}`}>
      <small>{label}</small>
      <strong>{formatCurrency(amount)}</strong>
      <span>{formatMetric(hours, "hours")}</span>
    </div>
  );
}

function AuthPanel({
  email,
  error,
  mode,
  onEmailChange,
  onModeChange,
  onPasswordChange,
  onSignOut,
  onSubmit,
  password,
  status,
  user
}: {
  email: string;
  error: string;
  mode: "login" | "register";
  onEmailChange: (value: string) => void;
  onModeChange: (value: "login" | "register") => void;
  onPasswordChange: (value: string) => void;
  onSignOut: () => void;
  onSubmit: () => void;
  password: string;
  status: string;
  user: UserAccount | null;
}) {
  if (user) {
    return (
      <section className="panel auth-panel">
        <div className="panel-head">
          <h2>Account</h2>
          <span className="count-pill">{status}</span>
        </div>
        <p className="account-email">{user.email}</p>
        <button className="ghost-button full" type="button" onClick={onSignOut}>
          Sign out
        </button>
      </section>
    );
  }

  return (
    <section className="panel auth-panel">
      <div className="panel-head">
        <h2>Account sync</h2>
        <span className="count-pill">{status}</span>
      </div>
      <p className="muted">Log in to save your calendar, departments, aliases, and preferences.</p>
      <div className="sort-buttons auth-mode">
        <button className={mode === "login" ? "active" : ""} type="button" onClick={() => onModeChange("login")}>Log in</button>
        <button className={mode === "register" ? "active" : ""} type="button" onClick={() => onModeChange("register")}>Register</button>
      </div>
      <label>
        Email
        <input value={email} type="email" autoComplete="email" onChange={(event) => onEmailChange(event.target.value)} />
      </label>
      <label>
        Password
        <input value={password} type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} onChange={(event) => onPasswordChange(event.target.value)} />
      </label>
      {error && <p className="error-note">{error}</p>}
      <button className="primary-button full" type="button" disabled={!email || password.length < 8} onClick={onSubmit}>
        {mode === "login" ? "Log in" : "Create account"}
      </button>
    </section>
  );
}

function DepartmentFilter({
  active,
  color,
  count,
  detail,
  label,
  onClick,
  onEdit
}: {
  active: boolean;
  color: string;
  count: number;
  detail?: string;
  label: string;
  onClick: () => void;
  onEdit?: () => void;
}) {
  return (
    <div className={`department-filter ${active ? "active" : ""}`}>
      <button type="button" onClick={onClick}>
        <span className="swatch" style={{ background: color }} />
        <span>
          <strong>{label}</strong>
          <small>{detail || `${count} lessons`}</small>
        </span>
      </button>
      {onEdit ? (
        <button className="icon-button" type="button" aria-label={`Edit ${label}`} onClick={onEdit}>
          <Edit3 size={16} />
        </button>
      ) : (
        <span className="count-pill">{count}</span>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="stat-card">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function LessonCard({
  compact,
  event,
  expanded,
  onAssignCode,
  onToggle
}: {
  compact: boolean;
  event: LessonEvent;
  expanded: boolean;
  onAssignCode: () => void;
  onToggle: () => void;
}) {
  const department = event.department || { name: "Unrecognized", color: "#8b8d94" };
  return (
    <article className={`lesson-card ${compact ? "compact" : ""}`}>
      <span className="lesson-accent" style={{ background: department.color }} />
      <button className="lesson-main" type="button" onClick={onToggle}>
        <span className="lesson-time">
          <Clock size={17} />
          {formatTimeRange(event)}
        </span>
        <h3>{event.topic || event.summary}</h3>
        <div className="lesson-tags">
          <span style={{ background: tintColor(department.color), color: department.color }}>{department.name}</span>
          {event.moduleCode && <span>{event.moduleCode}</span>}
          {event.delivery !== "unknown" && <span>{formatDelivery(event.delivery)}</span>}
        </div>
      </button>
      {!event.department && event.moduleCode && (
        <button className="map-button" type="button" onClick={onAssignCode}>
          <Plus size={16} />
          Map code
        </button>
      )}
      {expanded && !compact && (
        <div className="lesson-details">
          <p>{event.summary}</p>
          <dl>
            <div>
              <dt>Module code</dt>
              <dd>{event.moduleCode || "None"}</dd>
            </div>
            <div>
              <dt>Department</dt>
              <dd>{event.department?.name || "Unrecognized"}</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>{formatDelivery(event.delivery)}</dd>
            </div>
          </dl>
        </div>
      )}
    </article>
  );
}

function DepartmentDialog({
  departments,
  draft,
  onChange,
  onClose,
  onDelete,
  onSave
}: {
  departments: Department[];
  draft: DepartmentDraft;
  onChange: (draft: DepartmentDraft) => void;
  onClose: () => void;
  onDelete?: () => void;
  onSave: () => void;
}) {
  const codes = splitCodes(draft.codes);
  const duplicateCodes = codes.filter((code) =>
    departments.some((department) => department.id !== draft.id && department.codes.map((item) => item.toUpperCase()).includes(code))
  );
  const valid = draft.name.trim().length > 0 && codes.length > 0;

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="department-dialog" role="dialog" aria-modal="true" aria-labelledby="department-dialog-title">
        <div className="dialog-head">
          <div>
            <p className="eyebrow">Department mapping</p>
            <h2 id="department-dialog-title">{draft.id ? "Edit department" : "Add department"}</h2>
          </div>
          <button className="icon-button" type="button" aria-label="Close dialog" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <label>
          Department name
          <input value={draft.name} autoFocus onChange={(event) => onChange({ ...draft, name: event.target.value })} placeholder="e.g. UX Design" />
        </label>

        <label>
          Code or prefixes
          <input value={draft.codes} onChange={(event) => onChange({ ...draft, codes: event.target.value })} placeholder="e.g. UX900, UX" />
        </label>

        <label>
          Parent department
          <select value={draft.parentId || ""} onChange={(event) => onChange({ ...draft, parentId: event.target.value })}>
            <option value="">None - main department</option>
            {departments
              .filter((department) => department.id !== draft.id && !department.parentId)
              .map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
          </select>
        </label>

        <label className="course-kind-toggle">
          <input
            checked={Boolean(draft.isShortCourse)}
            type="checkbox"
            onChange={(event) => onChange({ ...draft, isShortCourse: event.target.checked })}
          />
          <span>
            <strong>Short course</strong>
            <small>Use intake labels instead of module labels in department insights.</small>
          </span>
        </label>

        {duplicateCodes.length > 0 && <p className="warning-note">Already used: {duplicateCodes.join(", ")}</p>}

        <label>
          Color
          <input value={draft.color} type="color" onChange={(event) => onChange({ ...draft, color: event.target.value })} />
        </label>

        <div className="color-grid">
          {colorOptions.map((color) => (
            <button
              key={color}
              className={draft.color.toLowerCase() === color.toLowerCase() ? "selected" : ""}
              type="button"
              aria-label={`Use color ${color}`}
              style={{ background: color }}
              onClick={() => onChange({ ...draft, color })}
            />
          ))}
        </div>

        <div className="dialog-preview">
          <span className="swatch" style={{ background: draft.color }} />
          <strong>{draft.name || "Department name"}</strong>
          <small>{formatDraftDepartmentDetail(draft, departments, codes)}</small>
        </div>

        <div className="dialog-actions">
          {onDelete && (
            <button className="danger-button" type="button" onClick={onDelete}>
              <Trash2 size={18} />
              Delete
            </button>
          )}
          <div />
          <button className="secondary-button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary-button" type="button" disabled={!valid} onClick={onSave}>
            <Save size={18} />
            Save
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ hasUpload }: { hasUpload: boolean }) {
  return (
    <section className="empty-state">
      <CalendarDays size={42} />
      <h2>{hasUpload ? "No lessons match the current filters" : "Upload an iCal file to begin"}</h2>
      <p>{hasUpload ? "Clear the search or choose a different department." : "The dashboard will show upcoming lessons, recognized departments, and mapping actions for unknown codes."}</p>
    </section>
  );
}

function RadialDepartments({ insights, metric }: { insights: InsightItem[]; metric: AnalyticsMetric }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const total = insights.reduce((sum, item) => sum + item.count, 0);
  if (!total) return <p className="muted">No lessons in this insight range yet.</p>;

  const activeDepartment = insights.find((item) => item.id === activeId);
  const activeModules = activeDepartment?.modules?.length ? activeDepartment.modules : [];
  const visibleSlices = activeModules.length ? activeModules : insights;
  const visibleTotal = Math.max(1, visibleSlices.reduce((sum, item) => sum + item.count, 0));
  const gradient = buildConicGradient(visibleSlices, visibleTotal);
  const selectedCount = activeDepartment ? activeDepartment.count : total;

  function selectDepartment(item: InsightItem) {
    const id = item.id || item.label;
    setActiveId((currentId) => (currentId === id ? null : id));
  }

  return (
    <div className="radial-wrap">
      <button
        className={`radial-chart ${activeDepartment ? "selected" : ""}`}
        type="button"
        style={{ background: `conic-gradient(${gradient})` }}
        onClick={() => activeDepartment && setActiveId(null)}
      >
        {!activeDepartment && <DonutHotspots insights={insights} onSelect={selectDepartment} />}
        <span>{formatMetricNumber(selectedCount, metric)}</span>
        <small>{activeDepartment ? activeDepartment.label : metric === "hours" ? "hours" : "lessons"}</small>
      </button>
      <div className="insight-list department-insight-list">
        {insights.map((item) => (
          <button
            key={item.id || item.label}
            className={`insight-row interactive ${activeDepartment?.id === item.id ? "active" : ""}`}
            aria-pressed={activeDepartment?.id === item.id}
            type="button"
            onClick={() => selectDepartment(item)}
          >
            <span className="swatch" style={{ background: item.color }} />
            <strong>{item.label}</strong>
            <small>{formatMetric(item.count, metric)}</small>
          </button>
        ))}
      </div>
      <div className="module-breakdown">
        <div>
          <h3>{activeDepartment ? `${activeDepartment.label} modules` : "Select a department"}</h3>
          <small>{activeDepartment ? formatInsightDetail(activeDepartment, metric) : "Click a department or chart slice to split the chart into module shades."}</small>
        </div>
        {(activeModules.length ? activeModules : visibleSlices.slice(0, 4)).map((module) => (
          <div key={module.id || module.label} className="module-row">
            <span className="swatch" style={{ background: module.color }} />
            <strong>{module.label}</strong>
            <small>{formatMetric(module.count, metric)}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutHotspots({ insights, onSelect }: { insights: InsightItem[]; onSelect: (item: InsightItem) => void }) {
  const total = insights.reduce((sum, item) => sum + item.count, 0);
  const radius = 41;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg className="radial-hotspots" viewBox="0 0 100 100" aria-hidden="true">
      {insights.map((item) => {
        const slice = total ? (item.count / total) * circumference : 0;
        const dashOffset = -offset;
        offset += slice;
        return (
          <circle
            key={item.id || item.label}
            cx="50"
            cy="50"
            fill="none"
            r={radius}
            stroke="transparent"
            strokeDasharray={`${slice} ${circumference - slice}`}
            strokeDashoffset={dashOffset}
            strokeWidth="20"
            transform="rotate(-90 50 50)"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(item);
            }}
          />
        );
      })}
    </svg>
  );
}

function MonthBars({ months, metric }: { months: MonthInsight[]; metric: AnalyticsMetric }) {
  const max = Math.max(1, ...months.map((month) => month.count));
  return (
    <div className="month-bars">
      {months.map((month) => (
        <div key={month.label} className="month-bar">
          <span style={{ height: `${Math.max(8, (month.count / max) * 100)}%` }} />
          <small>{month.label}</small>
          <strong>{formatMetricNumber(month.count, metric)}</strong>
        </div>
      ))}
    </div>
  );
}

function TopicBars({ topics, metric }: { topics: InsightItem[]; metric: AnalyticsMetric }) {
  const max = Math.max(1, ...topics.map((topic) => topic.count));
  if (!topics.length) return <p className="muted">No topics in this insight range yet.</p>;

  return (
    <div className="topic-bars">
      {topics.map((topic) => (
        <div key={topic.label} className="topic-row">
          <div>
            <strong>{topic.label}</strong>
            <small>{formatInsightDetail(topic, metric)}</small>
          </div>
          <span>
            <i style={{ width: `${(topic.count / max) * 100}%`, background: topic.color }} />
          </span>
        </div>
      ))}
    </div>
  );
}

function ThemeAliasManager({
  groups,
  onChange,
  rawThemes
}: {
  groups: LessonThemeGroup[];
  onChange: (groups: LessonThemeGroup[]) => void;
  rawThemes: RawLessonTheme[];
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [groupName, setGroupName] = useState("");
  const [selectedAliases, setSelectedAliases] = useState<string[]>([]);
  const [lessonSearch, setLessonSearch] = useState("");
  const [sortMode, setSortMode] = useState<"relevance" | "common">("relevance");
  const normalizedSelected = new Set(selectedAliases.map(normalizeThemeName));

  const rankedThemes = useMemo(() => {
    const search = normalizeThemeName(lessonSearch);
    return rawThemes
      .filter((theme) => !search || normalizeThemeName(theme.label).includes(search))
      .map((theme) => ({
        ...theme,
        relevance: groupName.trim() ? themeSimilarity(groupName, theme.label) : 0
      }))
      .sort((a, b) => {
        if (sortMode === "common") return b.lessons - a.lessons || b.hours - a.hours || a.label.localeCompare(b.label);
        return b.relevance - a.relevance || b.lessons - a.lessons || a.label.localeCompare(b.label);
      });
  }, [groupName, lessonSearch, rawThemes, sortMode]);

  function startNewGroup() {
    setEditingId(null);
    setGroupName("");
    setSelectedAliases([]);
    setLessonSearch("");
    setSortMode("relevance");
    setStep(1);
  }

  function editGroup(group: LessonThemeGroup) {
    setEditingId(group.id);
    setGroupName(group.name);
    setSelectedAliases(group.aliases);
    setLessonSearch("");
    setSortMode("relevance");
    setStep(2);
  }

  function toggleAlias(label: string) {
    setSelectedAliases((currentAliases) => {
      const normalized = normalizeThemeName(label);
      if (currentAliases.some((alias) => normalizeThemeName(alias) === normalized)) {
        return currentAliases.filter((alias) => normalizeThemeName(alias) !== normalized);
      }
      return [...currentAliases, label];
    });
  }

  function saveGroup() {
    const cleanName = groupName.trim();
    const aliases = normalizeAliasList([cleanName, ...selectedAliases]);
    if (!cleanName || aliases.length < 2) return;
    const nextGroup = { id: editingId || slugify(`theme-${cleanName}-${Date.now()}`), name: cleanName, aliases };
    onChange(editingId ? groups.map((group) => (group.id === editingId ? nextGroup : group)) : [...groups, nextGroup]);
    startNewGroup();
  }

  function removeGroup(id: string) {
    onChange(groups.filter((group) => group.id !== id));
  }

  return (
    <div className="theme-alias-manager">
      <div className="alias-head">
        <div>
          <h3>Theme aliases</h3>
          <p>Create a theme group, then choose which lesson names belong inside it.</p>
        </div>
        <span>{groups.length} group{groups.length === 1 ? "" : "s"}</span>
      </div>

      <div className="alias-stepper">
        <span className={step === 1 ? "active" : ""}>1. Name group</span>
        <span className={step === 2 ? "active" : ""}>2. Add lessons</span>
      </div>

      {step === 1 ? (
        <div className="alias-create-step">
          <label>
            Group name
            <input value={groupName} list="raw-theme-options" onChange={(event) => setGroupName(event.target.value)} placeholder="e.g. Bildbearbeitung" />
          </label>
          <button className="primary-button" type="button" disabled={!groupName.trim()} onClick={() => setStep(2)}>
            Continue
          </button>
          <datalist id="raw-theme-options">
            {rawThemes.map((theme) => (
              <option key={theme.label} value={theme.label} />
            ))}
          </datalist>
        </div>
      ) : (
        <div className="alias-picker-step">
          <div className="alias-picker-head">
            <div>
              <strong>{editingId ? "Editing" : "Creating"}: {groupName}</strong>
              <small>{normalizeAliasList([groupName, ...selectedAliases]).length} lesson name{normalizeAliasList([groupName, ...selectedAliases]).length === 1 ? "" : "s"} selected · {rankedThemes.length} lesson name{rankedThemes.length === 1 ? "" : "s"} shown</small>
            </div>
            <button className="ghost-button" type="button" onClick={() => setStep(1)}>
              Rename
            </button>
          </div>
          {selectedAliases.length > 0 && (
            <div className="selected-aliases">
              {selectedAliases.map((alias) => (
                <button key={alias} type="button" onClick={() => toggleAlias(alias)}>
                  {alias}
                  <X size={14} />
                </button>
              ))}
            </div>
          )}
          <div className="alias-picker-controls">
            <label>
              Search lessons
              <input value={lessonSearch} onChange={(event) => setLessonSearch(event.target.value)} placeholder="Search lesson names..." />
            </label>
            <div className="sort-buttons">
              <button className={sortMode === "relevance" ? "active" : ""} type="button" onClick={() => setSortMode("relevance")}>Relevant</button>
              <button className={sortMode === "common" ? "active" : ""} type="button" onClick={() => setSortMode("common")}>Common</button>
            </div>
          </div>
          <div className="alias-lesson-list">
            {rankedThemes.map((theme) => (
              <button key={theme.label} className={`alias-lesson-option ${normalizedSelected.has(normalizeThemeName(theme.label)) ? "selected" : ""}`} type="button" onClick={() => toggleAlias(theme.label)}>
                <span>
                  <strong>{theme.label}</strong>
                  <small>{theme.lessons} lesson{theme.lessons === 1 ? "" : "s"} · {formatMetric(theme.hours, "hours")}</small>
                </span>
                <small>{sortMode === "common" ? `${theme.lessons}x` : `${Math.round(theme.relevance * 100)}%`}</small>
              </button>
            ))}
          </div>
          <div className="alias-actions">
            <button className="ghost-button" type="button" onClick={startNewGroup}>Cancel</button>
            <button className="primary-button" type="button" disabled={!groupName.trim() || normalizeAliasList([groupName, ...selectedAliases]).length < 2} onClick={saveGroup}>
              <Save size={17} />
              Save group
            </button>
          </div>
        </div>
      )}

      {groups.length > 0 && (
        <div className="alias-groups">
          {groups.map((group) => (
            <div key={group.id} className="alias-group">
              <span>
                <strong>{group.name}</strong>
                <small>{group.aliases.join(", ")}</small>
              </span>
              <div className="alias-group-actions">
                <button className="icon-button" type="button" aria-label={`Edit ${group.name}`} onClick={() => editGroup(group)}>
                  <Edit3 size={16} />
                </button>
                <button className="icon-button" type="button" aria-label={`Delete ${group.name}`} onClick={() => removeGroup(group.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeekdayPattern({ days, metric }: { days: WeekdayInsight[]; metric: AnalyticsMetric }) {
  const max = Math.max(1, ...days.map((day) => day.count));
  if (!days.some((day) => day.count > 0)) return <p className="muted">No teaching days in this insight range yet.</p>;

  return (
    <div className="weekday-grid">
      {days.map((day) => (
        <div key={day.id || day.label} className={`weekday-card ${day.count > 0 ? "active" : ""}`}>
          <strong>{day.label}</strong>
          <span>
            <i style={{ height: `${Math.max(8, (day.count / max) * 100)}%`, background: day.color }} />
          </span>
          <small>{formatInsightDetail(day, metric)}</small>
        </div>
      ))}
    </div>
  );
}

function DeliveryTiles({ items, metric }: { items: InsightItem[]; metric: AnalyticsMetric }) {
  return (
    <div className="delivery-tiles">
      {items.map((item) => (
        <div key={item.label}>
          <span className="swatch" style={{ background: item.color }} />
          <strong>{formatMetricNumber(item.count, metric)}</strong>
          <small>{item.label} · {metric === "hours" ? "hours" : "lessons"}</small>
        </div>
      ))}
    </div>
  );
}

function groupByDay(events: LessonEvent[]) {
  return events.reduce((days, event) => {
    const day = new Date(event.start.getFullYear(), event.start.getMonth(), event.start.getDate()).toISOString();
    const dayEvents = days.get(day) || [];
    days.set(day, [...dayEvents, event]);
    return days;
  }, new Map<string, LessonEvent[]>());
}

type InsightItem = {
  id?: string;
  label: string;
  count: number;
  color: string;
  lessons: number;
  hours: number;
  sortOrder?: number;
  modules?: InsightItem[];
};

type MonthInsight = {
  label: string;
  count: number;
};

type WeekdayInsight = InsightItem & {
  dayIndex: number;
};

type InsightAccumulator = InsightItem & {
  moduleMap: Map<string, InsightItem>;
};

function buildDepartmentInsights(events: LessonEvent[], departments: Department[], grouped: boolean, metric: AnalyticsMetric): InsightItem[] {
  const counts = new Map<string, InsightAccumulator>();
  events.forEach((event) => {
    const department = getDisplayDepartment(event, departments, grouped);
    const id = department?.id || "unrecognized";
    const label = department?.name || "Unrecognized";
    const color = department?.color || "#8b8d94";
    const current = counts.get(id) || { id, label, count: 0, color, lessons: 0, hours: 0, modules: [], moduleMap: new Map<string, InsightItem>() };

    addEventToInsight(current, event, metric);

    const moduleDescriptor = getModuleDescriptor(event);
    const moduleLabel = moduleDescriptor.label;
    const moduleId = `${id}-${moduleDescriptor.id}`;
    const moduleIndex = current.moduleMap.size;
    const module = current.moduleMap.get(moduleDescriptor.id) || {
      id: moduleId,
      label: moduleLabel,
      count: 0,
      color: moduleColor(color, moduleIndex),
      lessons: 0,
      hours: 0,
      sortOrder: moduleDescriptor.sortOrder
    };
    addEventToInsight(module, event, metric);
    current.moduleMap.set(moduleDescriptor.id, module);
    counts.set(id, current);
  });

  return [...counts.values()]
    .map(({ moduleMap, ...item }) => ({
      ...item,
      modules: [...moduleMap.values()].sort(sortModuleInsights)
    }))
    .sort((a, b) => b.count - a.count);
}

function buildTopicInsights(events: LessonEvent[], metric: AnalyticsMetric, themeGroups: LessonThemeGroup[]): InsightItem[] {
  const colors = ["#1f6f78", "#7c3aed", "#dc2626", "#ec4899", "#f97316"];
  const counts = new Map<string, InsightItem>();
  events.forEach((event) => {
    const topic = getCanonicalThemeLabel(event.topic || "Untitled lesson", themeGroups);
    const current = counts.get(topic) || { id: topic, label: topic, count: 0, color: colors[counts.size % colors.length], lessons: 0, hours: 0 };
    addEventToInsight(current, event, metric);
    counts.set(topic, current);
  });
  return [...counts.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map((item, index) => ({ ...item, color: colors[index % colors.length] }));
}

function buildRawLessonThemes(events: LessonEvent[]) {
  const counts = new Map<string, RawLessonTheme>();
  events.forEach((event) => {
    const label = event.topic || "Untitled lesson";
    const current = counts.get(label) || { label, lessons: 0, hours: 0 };
    current.lessons += 1;
    current.hours += eventMetric(event, "hours");
    counts.set(label, current);
  });
  return [...counts.values()].sort((a, b) => b.lessons - a.lessons || a.label.localeCompare(b.label));
}

function getCanonicalThemeLabel(topic: string, groups: LessonThemeGroup[]) {
  const normalized = normalizeThemeName(topic);
  const group = groups.find((item) => [item.name, ...item.aliases].some((alias) => normalizeThemeName(alias) === normalized));
  return group?.name || topic;
}

function normalizeAliasList(values: string[]) {
  const seen = new Set<string>();
  return values
    .map((value) => value.trim())
    .filter(Boolean)
    .filter((value) => {
      const normalized = normalizeThemeName(value);
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function themeSimilarity(a: string, b: string) {
  const normalizedA = normalizeThemeName(a);
  const normalizedB = normalizeThemeName(b);
  if (!normalizedA || !normalizedB) return 0;
  if (normalizedA === normalizedB) return 1;
  if (normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA)) return 0.86;

  const tokensA = meaningfulThemeTokens(a);
  const tokensB = meaningfulThemeTokens(b);
  const shared = tokensA.filter((token) => tokensB.includes(token));
  if (!shared.length) return stringSimilarity(normalizedA, normalizedB);
  return Math.max(shared.length / Math.max(tokensA.length, tokensB.length), stringSimilarity(normalizedA, normalizedB));
}

function meaningfulThemeTokens(value: string) {
  const stopWords = new Set(["and", "der", "die", "das", "den", "dem", "des", "digital", "digitale", "lesson", "modul", "module", "und", "unterricht"]);
  return normalizeThemeName(value)
    .split(" ")
    .filter((token) => token.length > 2 && !stopWords.has(token));
}

function stringSimilarity(a: string, b: string) {
  const distance = levenshteinDistance(a, b);
  return 1 - distance / Math.max(a.length, b.length, 1);
}

function levenshteinDistance(a: string, b: string) {
  const previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let row = 1; row <= a.length; row += 1) {
    const current = [row];
    for (let column = 1; column <= b.length; column += 1) {
      current[column] = Math.min(
        previous[column] + 1,
        current[column - 1] + 1,
        previous[column - 1] + (a[row - 1] === b[column - 1] ? 0 : 1)
      );
    }
    previous.splice(0, previous.length, ...current);
  }
  return previous[b.length];
}

function normalizeThemeName(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/gi, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function buildDeliveryInsights(events: LessonEvent[], metric: AnalyticsMetric): InsightItem[] {
  const labels: Record<LessonEvent["delivery"], string> = {
    online: "Online",
    hybrid: "Hybrid",
    onCampus: "On campus",
    unknown: "Unspecified"
  };
  const colors: Record<LessonEvent["delivery"], string> = {
    online: "#38bdf8",
    hybrid: "#14b8a6",
    onCampus: "#1f6f78",
    unknown: "#8b8d94"
  };
  const counts = new Map<LessonEvent["delivery"], InsightItem>();
  events.forEach((event) => {
    const current = counts.get(event.delivery) || { id: event.delivery, label: labels[event.delivery], count: 0, color: colors[event.delivery], lessons: 0, hours: 0 };
    addEventToInsight(current, event, metric);
    counts.set(event.delivery, current);
  });
  return (["onCampus", "hybrid", "online", "unknown"] as LessonEvent["delivery"][])
    .map((key) => counts.get(key) || { id: key, label: labels[key], count: 0, color: colors[key], lessons: 0, hours: 0 })
    .filter((item) => item.count > 0);
}

function buildWeekdayInsights(events: LessonEvent[], metric: AnalyticsMetric): WeekdayInsight[] {
  const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const colors = ["#7c3aed", "#1f6f78", "#14b8a6", "#f97316", "#ec4899", "#dc2626", "#8b8d94"];
  const days = labels.map((label, index) => ({
    id: `weekday-${index}`,
    label,
    count: 0,
    color: colors[index],
    lessons: 0,
    hours: 0,
    dayIndex: index
  }));

  events.forEach((event) => {
    const index = (event.start.getDay() + 6) % 7;
    addEventToInsight(days[index], event, metric);
  });

  return days;
}

function addEventToInsight(item: InsightItem, event: LessonEvent, metric: AnalyticsMetric) {
  item.count += eventMetric(event, metric);
  item.lessons += 1;
  item.hours += eventMetric(event, "hours");
}

type ModuleDescriptor = {
  id: string;
  label: string;
  sortOrder: number;
};

function getModuleDescriptor(event: LessonEvent): ModuleDescriptor {
  const rawCode = (event.moduleCode || event.code || "").toUpperCase().replace(/\s+/g, "");
  if (isShortCourseEvent(event)) {
    const intake = getIntakeDescriptor(rawCode);
    if (intake) return intake;
    return { id: rawCode || "no-intake", label: rawCode || "No intake", sortOrder: Number.MAX_SAFE_INTEGER };
  }

  const moduleAliases: Record<string, ModuleDescriptor> = {
    "1": { id: "module-1", label: "Module 1", sortOrder: 1 },
    "4001": { id: "module-1", label: "Module 1", sortOrder: 1 },
    "4100": { id: "module-1", label: "Module 1", sortOrder: 1 },
    "2": { id: "module-2", label: "Module 2", sortOrder: 2 },
    "4002": { id: "module-2", label: "Module 2", sortOrder: 2 },
    "4200": { id: "module-2", label: "Module 2", sortOrder: 2 },
    "3": { id: "module-3", label: "Module 3", sortOrder: 3 },
    "4003": { id: "module-3", label: "Module 3", sortOrder: 3 },
    "4300": { id: "module-3", label: "Module 3", sortOrder: 3 },
    "4": { id: "module-4", label: "Module 4", sortOrder: 4 },
    "4004": { id: "module-4", label: "Module 4", sortOrder: 4 },
    "4400": { id: "module-4", label: "Module 4", sortOrder: 4 },
    "5": { id: "module-5", label: "Module 5", sortOrder: 5 },
    "5100": { id: "module-5", label: "Module 5", sortOrder: 5 },
    "5101": { id: "module-5", label: "Module 5", sortOrder: 5 },
    "6": { id: "module-6", label: "Module 6", sortOrder: 6 },
    "5203": { id: "module-6", label: "Module 6", sortOrder: 6 },
    "5300": { id: "module-6", label: "Module 6", sortOrder: 6 }
  };

  const alias = Object.keys(moduleAliases)
    .filter((code) => code.length > 1)
    .sort((a, b) => b.length - a.length)
    .find((code) => rawCode.includes(code));
  if (alias) return moduleAliases[alias];

  const singleDigitModule = rawCode.match(/^MODULE([1-6])$/)?.[1] || rawCode.match(/^MODUL([1-6])$/)?.[1];
  if (singleDigitModule) return moduleAliases[singleDigitModule];

  const moduleNumber = rawCode.match(/([456]\d{3})/)?.[1] || rawCode.match(/(\d{3,4})/)?.[1];
  if (moduleNumber) return { id: `module-${moduleNumber}`, label: `Module ${moduleNumber}`, sortOrder: Number(moduleNumber) };
  return { id: rawCode || "no-module", label: rawCode || "No module", sortOrder: Number.MAX_SAFE_INTEGER };
}

function getIntakeDescriptor(rawCode: string): ModuleDescriptor | null {
  const intakeCode = rawCode.match(/(\d{3,4})$/)?.[1];
  if (!intakeCode) return null;

  const normalized = intakeCode.length === 3 ? `0${intakeCode}` : intakeCode;
  const month = Number(normalized.slice(0, 2));
  const year = Number(normalized.slice(2, 4));
  if (month < 1 || month > 12 || !Number.isFinite(year)) return null;

  const date = new Date(2000 + year, month - 1, 1);
  const label = new Intl.DateTimeFormat(undefined, { month: "short", year: "numeric" }).format(date);
  return {
    id: `intake-${normalized}`,
    label: `Intake ${label}`,
    sortOrder: 10000 + (2000 + year) * 12 + month
  };
}

function isShortCourseEvent(event: LessonEvent) {
  const department = event.department;
  if (!department) return false;
  return Boolean(department.isShortCourse);
}

function sortModuleInsights(a: InsightItem, b: InsightItem) {
  const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return b.count - a.count || a.label.localeCompare(b.label);
}

function moduleColor(baseColor: string, index: number) {
  const shifts = [0.1, -0.14, 0.24, -0.28, 0.38, -0.42, 0.52, -0.06];
  return shadeHexColor(baseColor, shifts[index % shifts.length]);
}

function shadeHexColor(hex: string, amount: number) {
  const clean = hex.replace("#", "");
  if (!/^[\da-f]{6}$/i.test(clean)) return hex;

  const channels = [clean.slice(0, 2), clean.slice(2, 4), clean.slice(4, 6)].map((channel) => Number.parseInt(channel, 16));
  const target = amount >= 0 ? 255 : 0;
  const ratio = Math.abs(amount);
  const mixed = channels.map((channel) => Math.round(channel + (target - channel) * ratio));
  return `#${mixed.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

function buildConicGradient(items: InsightItem[], total: number) {
  let offset = 0;
  return items
    .map((item) => {
      const start = offset;
      const end = offset + (item.count / total) * 100;
      offset = end;
      return `${item.color} ${start}% ${end}%`;
    })
    .join(", ");
}

function buildMonthlyInsights(events: LessonEvent[], startDate: Date, endDate: Date, metric: AnalyticsMetric): MonthInsight[] {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const rawCount = monthDiff(start, end) + 1;
  if (rawCount > 36) {
    const years = Array.from({ length: end.getFullYear() - start.getFullYear() + 1 }, (_, index) => ({
      year: start.getFullYear() + index,
      label: String(start.getFullYear() + index),
      count: 0
    }));
    events.forEach((event) => {
      const match = years.find((year) => year.year === event.start.getFullYear());
      if (match) match.count += eventMetric(event, metric);
    });
    return years.map(({ label, count }) => ({ label, count }));
  }

  const count = Math.max(1, rawCount);
  const months = Array.from({ length: count }, (_, index) => {
    const date = new Date(start.getFullYear(), start.getMonth() + index, 1);
    return {
      date,
      label: new Intl.DateTimeFormat(undefined, { month: "short", year: count > 12 ? "2-digit" : undefined }).format(date),
      count: 0
    };
  });

  events.forEach((event) => {
    const match = months.find((month) => month.date.getFullYear() === event.start.getFullYear() && month.date.getMonth() === event.start.getMonth());
    if (match) match.count += eventMetric(event, metric);
  });

  return months.map(({ label, count }) => ({ label, count }));
}

function eventMetric(event: LessonEvent, metric: AnalyticsMetric) {
  if (metric === "lessons") return 1;
  if (!event.end) return 0;
  return Math.max(0, (event.end.getTime() - event.start.getTime()) / 3600000);
}

function sumMetric(events: LessonEvent[], metric: AnalyticsMetric) {
  return events.reduce((sum, event) => sum + eventMetric(event, metric), 0);
}

function buildEarningsSummary(events: LessonEvent[], today: Date, hourlyWage: string) {
  const wage = parseHourlyWage(hourlyWage);
  const pastHours = sumMetric(events.filter((event) => event.start < today), "hours");
  const futureHours = sumMetric(events.filter((event) => event.start >= today), "hours");
  const totalHours = pastHours + futureHours;
  return {
    pastHours,
    futureHours,
    totalHours,
    pastAmount: pastHours * wage,
    futureAmount: futureHours * wage,
    totalAmount: totalHours * wage
  };
}

function parseHourlyWage(value: string) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat(undefined, { currency: "CHF", maximumFractionDigits: 0, style: "currency" }).format(value);
}

function formatMetric(value: number, metric: AnalyticsMetric) {
  const unit = metric === "hours" ? "h" : value === 1 ? "lesson" : "lessons";
  return `${formatMetricNumber(value, metric)} ${unit}`;
}

function formatInsightDetail(item: InsightItem, metric: AnalyticsMetric) {
  const lessonText = `${item.lessons} lesson${item.lessons === 1 ? "" : "s"}`;
  if (metric === "hours") return `${formatMetric(item.count, metric)} · ${lessonText}`;
  return `${formatMetric(item.count, metric)} · ${formatMetric(item.hours, "hours")}`;
}

function formatMetricNumber(value: number, metric: AnalyticsMetric) {
  if (metric === "lessons") return String(Math.round(value));
  if (value === 0) return "0";
  return Number.isInteger(value) ? String(value) : value.toFixed(value < 10 ? 1 : 0);
}

function getDisplayDepartment(event: LessonEvent, departments: Department[], grouped: boolean) {
  if (!event.department) return null;
  if (!grouped || !event.department.parentId) return event.department;
  return getDepartmentById(departments, event.department.parentId) || event.department;
}

function getDepartmentById(departments: Department[], id?: string) {
  return departments.find((department) => department.id === id) || null;
}

function formatDepartmentDetail(department: Department, departments: Department[]) {
  const parts = [];
  if (department.parentId) parts.push(getDepartmentById(departments, department.parentId)?.name || "Sub-department");
  if (department.isShortCourse) parts.push("Short course");
  parts.push(department.codes.join(", "));
  return parts.filter(Boolean).join(" · ");
}

function formatDraftDepartmentDetail(draft: DepartmentDraft, departments: Department[], codes: string[]) {
  const parts = [];
  if (draft.parentId) parts.push(getDepartmentById(departments, draft.parentId)?.name || "Parent");
  if (draft.isShortCourse) parts.push("Short course");
  parts.push(codes.join(", ") || "Code or prefixes");
  return parts.join(" · ");
}

function loadLessonThemeGroups(): LessonThemeGroup[] {
  const saved = localStorage.getItem(LESSON_THEME_STORAGE_KEY);
  if (!saved) return [];
  try {
    const parsed = JSON.parse(saved) as LessonThemeGroup[];
    return parsed
      .filter((group) => group.name && Array.isArray(group.aliases))
      .map((group) => ({
        id: group.id || slugify(`theme-${group.name}`),
        name: group.name,
        aliases: normalizeAliasList(group.aliases.length ? group.aliases : [group.name])
      }));
  } catch {
    return [];
  }
}

function persistLessonThemeGroups(groups: LessonThemeGroup[]) {
  localStorage.setItem(LESSON_THEME_STORAGE_KEY, JSON.stringify(groups));
}

function getAnalyticsWindow(events: LessonEvent[], today: Date, range: AnalyticsRange, scope: AnalyticsScope, customStart: string, customEnd: string) {
  if (range === "custom" && customStart && customEnd) {
    return { start: parseDateInput(customStart), end: endOfDay(parseDateInput(customEnd)) };
  }

  if (range === "all") {
    const times = events.map((event) => event.start.getTime());
    return {
      start: new Date(Math.min(...times, today.getTime())),
      end: new Date(Math.max(...times, today.getTime()))
    };
  }

  const months = range === "3m" ? 3 : 12;
  if (scope === "future") {
    return { start: today, end: addMonths(today, months) };
  }
  if (scope === "both") {
    return { start: addMonths(today, -months), end: addMonths(today, months) };
  }
  return { start: addMonths(today, -months), end: today };
}

function parseDateInput(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function endOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function addMonths(date: Date, months: number) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function monthDiff(start: Date, end: Date) {
  return (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
}

function formatShortDate(date: Date) {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "2-digit" }).format(date);
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function formatDelivery(value: LessonEvent["delivery"]) {
  if (value === "onCampus") return "On campus";
  if (value === "online") return "Online";
  if (value === "hybrid") return "Hybrid";
  return "Unspecified";
}

function formatDay(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

function formatTimeRange(event: LessonEvent) {
  const start = formatTime(event.start);
  return event.end ? `${start} - ${formatTime(event.end)}` : start;
}

function formatTime(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function loadThemeMode(): ThemeMode {
  const stored = localStorage.getItem("lesson-theme-mode");
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
